import {
  Dropdown,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
} from '@carbon/react';
import { Information, ExpandAll, CollapseAll } from '@carbon/react/icons';
import { GeometryData, BurdenedLeaseComparison } from './types';
import { useState, useMemo } from 'react';
import { GeometryRow } from './geometry-row';
import { InterestComparison } from './atoms';
import { formatNumber } from 'utils/number';
import { FloatingTooltip } from 'components/floating-tooltip';
import style from './reconciler.module.scss';

type ReconcilerTableProps = {
  comparisonResults: InterestComparison[];
  burdenedLeaseComparisons: BurdenedLeaseComparison[];
  tolerance: number;
  workspaces: Record<number, { name: string; lastCalculated: string | null }>;
};

const HeaderWithTooltip = ({
  label,
  tooltip,
}: {
  label: string;
  tooltip: string;
}) => (
  <span className={style.headerWithTooltip}>
    {label}
    <FloatingTooltip content={tooltip} placement="top">
      <Information />
    </FloatingTooltip>
  </span>
);

export const ReconcilerTable = ({
  comparisonResults,
  burdenedLeaseComparisons,
  tolerance,
  workspaces,
}: ReconcilerTableProps) => {
  const [expandedGeometries, setExpandedGeometries] = useState<Set<string>>(
    new Set()
  );
  const [statusFilter, setStatusFilter] = useState<
    | 'all'
    | 'all_correct'
    | 'all_incorrect'
    | 'interest_incorrect'
    | 'shape_incorrect'
  >('all');

  const statusFilterItems = [
    { id: 'all', label: 'All' },
    { id: 'all_correct', label: 'Match' },
    { id: 'all_incorrect', label: 'Mismatch' },
    { id: 'interest_incorrect', label: 'Interest Mismatch' },
    { id: 'shape_incorrect', label: 'Shape Mismatch' },
  ];

  const toggleGeometry = (geometryName: string) => {
    setExpandedGeometries((prev) => {
      const next = new Set(prev);
      if (next.has(geometryName)) {
        next.delete(geometryName);
      } else {
        next.add(geometryName);
      }
      return next;
    });
  };

  // Group comparisons by geometry and shape combination
  const geometryData = useMemo(() => {
    const grouped = new Map<string, GeometryData>();

    comparisonResults.forEach((comparison) => {
      const geomName = comparison.geometryName;
      // Include workspaceIds in the key to differentiate shapes from different workspaces
      const shapeKey = `${comparison.shapeIds
        .sort()
        .join('+')}_${comparison.workspaceIds.sort().join('+')}`;

      if (!grouped.has(geomName)) {
        grouped.set(geomName, {
          geometryName: geomName,
          shapes: [],
          totalComparisons: 0,
          matched: 0,
          failed: 0,
          interestMatched: 0,
          interestFailed: 0,
          shapeMatched: 0,
          shapeFailed: 0,
        });
      }

      const geomData = grouped.get(geomName)!;

      // Find or create shape group
      let shapeGroup = geomData.shapes.find(
        (s) =>
          `${s.shapeIds.sort().join('+')}_${s.workspaceIds
            .sort()
            .join('+')}` === shapeKey
      );

      if (!shapeGroup) {
        shapeGroup = {
          shapeIds: comparison.shapeIds,
          workspaceIds: comparison.workspaceIds,
          shapeNames: comparison.shapeNames,
          interestComparisons: [],
          matched: 0,
          failed: 0,
          lastCalculated:
            workspaces[comparison.workspaceIds[0]]?.lastCalculated ?? null,
        };
        geomData.shapes.push(shapeGroup);
      }

      shapeGroup.interestComparisons.push(comparison);
      geomData.totalComparisons++;

      if (comparison.match) {
        geomData.matched++;
        shapeGroup.matched++;
        geomData.interestMatched++;
      } else {
        geomData.failed++;
        shapeGroup.failed++;
        geomData.interestFailed++;
      }

      if (comparison.shapeMatch) {
        geomData.shapeMatched++;
      } else {
        geomData.shapeFailed++;
      }
    });

    burdenedLeaseComparisons.forEach((leaseComparison) => {
      const geomName = leaseComparison.geometryName;
      if (grouped.has(geomName)) {
        grouped.get(geomName)!.burdenedLeaseComparison = leaseComparison;
      }
    });

    return Array.from(grouped.values());
  }, [comparisonResults, burdenedLeaseComparisons, workspaces]);

  const filteredGeometryData = useMemo(() => {
    if (statusFilter === 'all') return geometryData;

    return geometryData
      .map((geom) => {
        // Filter shapes based on their comparison results
        const filteredShapes = geom.shapes
          .map((shape) => {
            const filteredComparisons = shape.interestComparisons.filter(
              (c) => {
                if (statusFilter === 'all_correct')
                  return c.match && c.shapeMatch;
                if (statusFilter === 'all_incorrect')
                  return !c.match && !c.shapeMatch;
                if (statusFilter === 'interest_incorrect') return !c.match;
                if (statusFilter === 'shape_incorrect') return !c.shapeMatch;
                return true;
              }
            );
            if (filteredComparisons.length === 0) return null;
            return {
              ...shape,
              interestComparisons: filteredComparisons,
              matched: filteredComparisons.filter((c) => c.match).length,
              failed: filteredComparisons.filter((c) => !c.match).length,
            };
          })
          .filter(Boolean) as typeof geom.shapes;

        if (filteredShapes.length === 0) return null;

        const allFilteredComparisons = filteredShapes.flatMap(
          (s) => s.interestComparisons
        );
        const totalMatched = allFilteredComparisons.filter(
          (c) => c.match
        ).length;
        const totalFailed = allFilteredComparisons.filter(
          (c) => !c.match
        ).length;
        const interestMatched = allFilteredComparisons.filter(
          (c) => c.match
        ).length;
        const interestFailed = allFilteredComparisons.filter(
          (c) => !c.match
        ).length;
        const shapeMatched = allFilteredComparisons.filter(
          (c) => c.shapeMatch
        ).length;
        const shapeFailed = allFilteredComparisons.filter(
          (c) => !c.shapeMatch
        ).length;

        return {
          ...geom,
          shapes: filteredShapes,
          totalComparisons: totalMatched + totalFailed,
          matched: totalMatched,
          failed: totalFailed,
          interestMatched,
          interestFailed,
          shapeMatched,
          shapeFailed,
        };
      })
      .filter(Boolean) as GeometryData[];
  }, [geometryData, statusFilter]);

  const expandAll = () => {
    setExpandedGeometries(
      new Set(filteredGeometryData.map((g) => g.geometryName))
    );
  };

  const collapseAll = () => {
    setExpandedGeometries(new Set());
  };

  if (comparisonResults.length === 0) {
    return (
      <div className={style.noComparisons}>
        <p>No spatial overlaps found to compare.</p>
      </div>
    );
  }

  return (
    <div className={style.tableContainer}>
      <div className={style.tableScrollContainer}>
        <div className={style.tableCard}>
          <div className={style.tableControls}>
            <h3>Geometry Comparisons</h3>
            <div className={style.tableControlsRight}>
              <div className={style.statusFilter}>
                <span className={style.statusFilterLabel}>Match Filter:</span>
                <Dropdown
                  id="status-filter"
                  titleText=""
                  label="Filter by status"
                  items={statusFilterItems}
                  itemToString={(item) => (item ? item.label : '')}
                  selectedItem={statusFilterItems.find(
                    (item) => item.id === statusFilter
                  )}
                  onChange={({ selectedItem }) => {
                    if (selectedItem) {
                      setStatusFilter(
                        selectedItem.id as
                          | 'all'
                          | 'all_correct'
                          | 'all_incorrect'
                          | 'interest_incorrect'
                          | 'shape_incorrect'
                      );
                    }
                  }}
                  size="sm"
                />
              </div>
              <Button
                kind="ghost"
                renderIcon={ExpandAll}
                onClick={expandAll}
                className={style.expandCollapseButton}
              >
                Expand All
              </Button>
              <Button
                kind="ghost"
                renderIcon={CollapseAll}
                onClick={collapseAll}
                className={style.expandCollapseButton}
              >
                Collapse All
              </Button>
            </div>
          </div>

          <div className={style.tableWrapper}>
            <Table>
              <TableHead className={style.stickyTableHead}>
                <TableRow>
                  <TableHeader className={style.noWrapHeader}>
                    Title Validator Output Shape
                  </TableHeader>
                  <TableHeader className={style.noWrapHeader}>
                    Entity Title Asset Shape(s)
                  </TableHeader>
                  <TableHeader className={style.noWrapHeader}>
                    <HeaderWithTooltip
                      label="Shape Match"
                      tooltip="How similar the shapes are (IoU). 100% means identical shapes, lower values indicate differences in size or position."
                    />
                  </TableHeader>
                  <TableHeader className={style.noWrapHeader}>
                    Interest Type
                  </TableHeader>
                  <TableHeader className={style.noWrapHeader}>
                    Entity Title Asset Value
                  </TableHeader>
                  <TableHeader className={style.noWrapHeader}>
                    Title Validator Value
                  </TableHeader>
                  <TableHeader className={style.noWrapHeader}>
                    <HeaderWithTooltip
                      label="Interest Difference"
                      tooltip={`Values are considered matching if they differ by less than ${formatNumber(
                        tolerance,
                        8,
                        8,
                        'decimal',
                        false
                      )}`}
                    />
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGeometryData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className={style.noResults}>
                      No comparisons found matching filter
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGeometryData.map((geom) => (
                    <GeometryRow
                      key={geom.geometryName}
                      geometry={geom}
                      expandedGeometries={expandedGeometries}
                      toggleGeometry={toggleGeometry}
                      workspaces={workspaces}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};
